/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/subscriptionStatsTable.scss';

import {AwesomeIcon, ChangeFunction, CloseButton, Loader, SelectItemShape, WithBackground} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import React, {MouseEventHandler} from 'react';
import SubscriptionStats from '../../../models/subscriptionStatsModel';
import {DataSplitMethod} from "../../../util/interfaces";
import {BolmateSelect} from "../../controls";

interface Props {
    onClose: MouseEventHandler;
    onChangePage: (page: number) => MouseEventHandler;
    onChangeSplitMethod: ChangeFunction<DataSplitMethod>;
    stats: SubscriptionStats[];
    loading: boolean;
    active: boolean;
    page: number;
    total: number;
    splitMethod: DataSplitMethod;
}

export default function SubscriptionStatsTable(props: Props) {
    const {
            stats, loading, page, active, total, onClose, onChangePage, onChangeSplitMethod, splitMethod
        } = props,
        hasMore = (page + 1) * 20 < total, hasLess = page > 0,
        splitMethodItems: SelectItemShape[] = [
            {value: DataSplitMethod.DAY, label: 'Per dag'},
            {value: DataSplitMethod.WEEK, label: 'Per week'},
            {value: DataSplitMethod.MONTH, label: 'Per maand'},
            {value: DataSplitMethod.QUARTER, label: 'Per kwartaal'},
            {value: DataSplitMethod.YEAR, label: 'Per jaar'},
        ];

    function renderStat(current: number, gain: number, positiveIsRed: boolean = false) {
        const token = gain >= 0 ? '+' : '',
            color = gain === 0 ? null : gain > 0 ? positiveIsRed ? 'red' : 'green' : positiveIsRed ? 'green' : 'red';
        return <label>{current} <span
            className={classNames('gain', color)}>{token}{gain}</span></label>
    }

    let dateLabel: string;
    switch (splitMethod) {
        case DataSplitMethod.DAY:
        case DataSplitMethod.HOUR:
            dateLabel = 'Dag';
            break;
        case DataSplitMethod.MONTH:
            dateLabel = 'Maand';
            break;
        case DataSplitMethod.QUARTER:
            dateLabel = 'Kwartaal';
            break;
        case DataSplitMethod.WEEK:
            dateLabel = 'Week';
            break;
        case DataSplitMethod.YEAR:
            dateLabel = 'Jaar';
            break;
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='subscription-stats-table default-popup'>
                <div className='header-container'>
                    <label className='header-title'>Abonnementstatussen</label>
                    <CloseButton onClick={onClose}/>
                </div>
                <div className='body-container'>
                    <div className='list-header'>
                        <BolmateSelect value={splitMethod} onChange={onChangeSplitMethod} items={splitMethodItems}
                                       label='Weergave' className='display-select'/>
                    </div>
                    <div className='list'>
                        <Loader active={loading}/>
                        <div className='row head'>
                            <label>{dateLabel}</label>
                            <label>Actief</label>
                            <label>Proefperiode</label>
                            <label>Gratis</label>
                            <label>Opgezegd</label>
                            <label>Proef verlopen</label>
                            <label>Inactief</label>
                        </div>
                        {stats.map((stat: SubscriptionStats, index: number) => {
                            const {
                                simpleStats, activeGain, inactiveGain, freeGain, trialGain,
                                canceledGain, expiredGain
                            } = stat;
                            return (
                                <div className='row' key={index}>
                                    <label>{stat.periodLabel}</label>
                                    {renderStat(simpleStats.active, activeGain)}
                                    {renderStat(simpleStats.trial, trialGain)}
                                    {renderStat(simpleStats.free, freeGain)}
                                    {renderStat(simpleStats.canceled, canceledGain, true)}
                                    {renderStat(simpleStats.expired, expiredGain, true)}
                                    {renderStat(simpleStats.inactive, inactiveGain, true)}
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div className='footer-container'>
                    <label className='page-label'>Pagina {page + 1}</label>
                    <div className='pager-container'>
                        <AwesomeIcon name='chevron-left' className={classNames('pager', !hasLess && 'disabled')}
                                     onClick={hasLess ? onChangePage(page - 1) : undefined}/>
                        <AwesomeIcon name='chevron-right' className={classNames('pager', !hasMore && 'disabled')}
                                     onClick={hasMore ? onChangePage(page + 1) : undefined}/>
                    </div>
                </div>
            </div>
        </WithBackground>
    )
}